import { ENV_PROD } from 'config';

// Base interface for all URL params
interface UrlParams {
  site: string;
  includeCanonical?: boolean;
}

interface CategoryParams extends UrlParams {
  slug: string;
}

interface CategoryPageParams extends CategoryParams {
  pageNumber: number | string;
}

interface PostParams extends UrlParams {
  slug: string;
}

interface SearchParams extends UrlParams {
  term: string;
}

/**
 * Gets the canonical domain base URL
 */
export function getCanonicalPath(): string {
  if (typeof window !== 'undefined') {
    // Fallback to hostname detection
    const hostname = window.location.hostname;
    return hostname;
  }
  // Server-side: can safely use process.env
  return process.env?.KAVAK_ENVIRONMENT === ENV_PROD
    ? `https://www.kavak.com`
    : `https://www.kavak-frontend.dev.kavak.io`;
}

/**
 * Generates the appropriate URL path for blog home based on site
 */
export function getBlogHomePath({
  site,
  includeCanonical = false,
}: UrlParams): string {
  const path = `/${site}/blog`;
  return includeCanonical ? `${getCanonicalPath()}${path}` : path;
}

/**
 * Generates the appropriate URL path for blog categories based on site and slug
 */
export function getCategoryPath({
  site,
  slug,
  includeCanonical = false,
}: CategoryParams): string {
  const path = `/${site}/blog/categorias/${slug}`;
  return includeCanonical ? `${getCanonicalPath()}${path}` : path;
}

/**
 * Generates the appropriate URL path for paginated category pages
 */
export function getCategoryPagePath({
  site,
  slug,
  pageNumber,
  includeCanonical = false,
}: CategoryPageParams): string {
  const path = `/${site}/blog/categorias/${slug}/page/${pageNumber}`;
  return includeCanonical ? `${getCanonicalPath()}${path}` : path;
}

/**
 * Generates the appropriate URL path for blog posts based on site and slug
 */
export function getPostPath({
  site,
  slug,
  includeCanonical = false,
}: PostParams): string {
  const path = `/${site}/blog/${slug}`;
  return includeCanonical ? `${getCanonicalPath()}${path}` : path;
}

/**
 * Generates the appropriate URL path for search results based on site and search term
 */
export function getSearchPath({
  site,
  term,
  includeCanonical = false,
}: SearchParams): string {
  const path = `/${site}/blog/search/${term}`;
  return includeCanonical ? `${getCanonicalPath()}${path}` : path;
}

/**
 * Generates the appropriate URL path for the seller section based on site
 */
export function getSellerKavakPath({
  site,
  includeCanonical = true,
}: UrlParams): string {
  const pathSegments: { [key: string]: string } = {
    mx: 'vender-mi-auto',
    br: 'vender-carro',
    ar: 'vender',
    cl: 'vende-tu-auto',
  };

  const pathSegment = pathSegments[site];
  let path = `/${site}/${pathSegment}`;

  if (!pathSegment) {
    path = `/${site}`;
  }

  return includeCanonical ? `${getCanonicalPath()}${path}` : path;
}

/**
 * Generates the appropriate URL path for the buying section based on site
 */
export function getBuyKavakPath({
  site,
  includeCanonical = true,
}: UrlParams): string {
  const pathSegments: { [key: string]: string } = {
    br: 'seminovos',
    ar: 'usados',
    cl: 'usados',
    mx: 'seminuevos',
  };

  const pathSegment = pathSegments[site]; // No default
  let path = `/${site}/${pathSegment}`;

  if (!pathSegment) {
    path = `/${site}`;
  }

  return includeCanonical ? `${getCanonicalPath()}${path}` : path;
}
